/**
 * Format date string for display without timezone conversion issues
 * 
 * When displaying date-only strings (e.g., "2026-02-27"), using new Date() can cause
 * timezone conversion problems where the date shifts by one day.
 * 
 * This function extracts the date components directly from the string and creates
 * a Date object in the local timezone to avoid these issues.
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO format
 * @returns {string} Formatted date string in locale format
 */
export const formatDateDisplay = (dateString) => {
  if (!dateString) return '';
  
  // Extract date parts directly from string to avoid timezone conversion
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [_, year, month, day] = match;
    // Create date in local timezone (month is 0-indexed)
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString();
  }
  
  // Fallback for other formats (should rarely be used)
  return new Date(dateString).toLocaleDateString();
};

/**
 * Format date string for input fields (YYYY-MM-DD format)
 * 
 * Extracts just the date part without any timezone conversion
 * 
 * @param {string} dateString - Date string in any format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // For ISO datetime strings, extract just the date part
  return dateString.split('T')[0];
};
